<script>
  export let values = null;
  export let total = null;
  export let previousValues = null;
  export let previousTotal = null;
  export let maxPercent = 100;
  export let currentCapacity = null;
  export let tooltipContent = null;
  export let onTooltipMouseOver = null;
  export let onTooltipMouseOut = null;
  export let onTooltipMouseMove = null;

  const capacities = [1, 2, 3, 4, 5];

  function normalizeNumber(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  function getPercent(sourceValues, sourceTotal, capacity) {
    if (!sourceValues) return 0;

    const rawValue = normalizeNumber(sourceValues[capacity]);
    const normalizedTotal = normalizeNumber(sourceTotal);

    if (normalizedTotal > 0) {
      return Math.round((rawValue / normalizedTotal) * 100);
    }

    return Math.max(0, Math.min(100, Math.round(rawValue)));
  }

  function getScaledHeight(percent, maxValue) {
    const normalizedMaxPercent = normalizeNumber(maxValue) || 100;
    const normalizedPercent = normalizeNumber(percent);

    return Math.max(0, Math.min(100, (normalizedPercent / normalizedMaxPercent) * 100));
  }

  function clampCapacity(value) {
    const numericValue = Math.ceil(normalizeNumber(value));
    return numericValue >= 1 && numericValue <= 5 ? numericValue : null;
  }

  function getCapacityPosition(value) {
    const normalizedValue = clampCapacity(value);
    if (!normalizedValue) return 0;

    return ((normalizedValue - 1) * 20) + 10;
  }

  function hasComparisonData(sourceValues, sourceTotal) {
    return !!sourceValues && Number.isFinite(Number(sourceTotal)) && Number(sourceTotal) > 0;
  }

  function getAccessibleLabel(item) {
    const comparisonText = showPreviousComparison
      ? `, previous ${item.comparisonPercent}%`
      : '';

    return `Capacity ${item.capacity}: current ${item.currentPercent}%${comparisonText}`;
  }

  $: resolvedCurrentCapacity = clampCapacity(currentCapacity);
  $: showPreviousComparison = hasComparisonData(previousValues, previousTotal);
  $: rawItems = capacities.map((capacity) => {
    const currentPercent = getPercent(values, total, capacity);
    const comparisonPercent = showPreviousComparison
      ? getPercent(previousValues, previousTotal, capacity)
      : currentPercent;

    return {
      capacity,
      currentPercent,
      comparisonPercent
    };
  });
  $: highestPercent = rawItems.reduce((maxValue, item) => {
    const comparisonMax = showPreviousComparison ? item.comparisonPercent : 0;
    return Math.max(maxValue, item.currentPercent, comparisonMax);
  }, 0);
  $: resolvedMaxPercent = Math.max(normalizeNumber(maxPercent) || 100, highestPercent);
  $: items = rawItems.map((item) => ({
    ...item,
    currentHeight: getScaledHeight(item.currentPercent, resolvedMaxPercent),
    comparisonHeight: getScaledHeight(item.comparisonPercent, resolvedMaxPercent)
  }));
  $: indexPosition = getCapacityPosition(resolvedCurrentCapacity);
</script>

<div class="capacity-distribution-columns">
  <div class="capacity-distribution-columns__chart">
    <div class="capacity-distribution-columns__bars">
      {#each items as item}
        <button
          type="button"
          class="capacity-distribution-columns__item"
          data-capacity={item.capacity}
          data-tooltip={tooltipContent ? tooltipContent(item.capacity) : undefined}
          aria-label={getAccessibleLabel(item)}
          on:mouseover={onTooltipMouseOver}
          on:mouseout={onTooltipMouseOut}
          on:focus={onTooltipMouseOver}
          on:blur={onTooltipMouseOut}
          on:mousemove={onTooltipMouseMove}
        >
          <span class="capacity-distribution-columns__stack">
            <span
              class="capacity-distribution-columns__bar-current"
              style={`height: ${item.currentHeight}%;`}
            >
            </span>
            {#if showPreviousComparison && item.comparisonHeight > 0}
              <span
                class="capacity-distribution-columns__bar-previous"
                style={`height: ${item.comparisonHeight}%;`}
              >
                <span class="capacity-distribution-columns__bar-previous-dot"></span>
              </span>
            {/if}
          </span>
        </button>
      {/each}

      {#if resolvedCurrentCapacity}
        <div
          class="capacity-distribution-columns__index"
          data-capacity={resolvedCurrentCapacity}
          style={`left: ${indexPosition}%;`}
        >
          <svg
            class="capacity-distribution-columns__index-icon"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              class="capacity-distribution-columns__index-shape"
              d="M10 19.2C10 19.2 2.8 13.4 2.8 8.7C2.8 4.81 6.02 1.6 10 1.6C13.98 1.6 17.2 4.81 17.2 8.7C17.2 13.4 10 19.2 10 19.2Z"
            />
            <circle
              class="capacity-distribution-columns__index-center"
              cx="10"
              cy="8.7"
              r="3.1"
            />
          </svg>
        </div>
      {/if}
    </div>
  </div>
</div>
